/**
 * Payment method picker — controlled radio-style selector for saved payment
 * methods. No form wrapper — just renders the grid and calls onChange when the
 * user picks a card. A member with no cards on file gets the same grid, down
 * to the Add card tile (and tap-to-pay when offered).
 *
 * "Add card" expands an inline Stripe Elements form rather than opening a
 * nested dialog, since this component is typically rendered inside a dialog.
 *
 * Auto-selects the default payment method on mount if no value is provided.
 */
import { CreditCard, Plus, Smartphone } from 'lucide-react'
import { useEffect } from 'react'

import {
  formatCardBrand,
  formatCardNumber,
  type MemberPaymentMethod,
  type PostMemberSetupIntentResponse,
} from '@snootsvet/shared'

import { Badge } from '@/frontend/components/ui/badge'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { AddPaymentMethodForm } from '@/frontend/features/orders/add-payment-method-form'
import { useCreateSetupIntent } from '@/frontend/features/orders/hooks/use-create-setup-intent'
import { useListMemberPaymentMethods } from '@/frontend/features/orders/hooks/use-list-member-payment-methods'
import { cn } from '@/frontend/lib/utils'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Sentinel `value` for the in-person tap option, which has no Stripe payment method id. */
export const TAP_TO_PAY_METHOD = 'tap-to-pay'

interface PaymentMethodPickerProps {
  /** The member whose payment methods to list. */
  memberId: string
  /** Currently selected payment method ID, or `TAP_TO_PAY_METHOD`. */
  value: string | null
  /** Called when the user selects a payment method. */
  onChange: (paymentMethodId: string) => void
  /** Offers an in-person tap-to-pay option alongside the saved cards. */
  allowTapToPay?: boolean
}

/** Controlled payment method selector — renders saved cards as selectable options. */
export function PaymentMethodPicker({
  memberId,
  value,
  onChange,
  allowTapToPay = false,
}: PaymentMethodPickerProps) {
  const { data: methods, isLoading, refetch } = useListMemberPaymentMethods(memberId)
  const {
    mutate: createSetupIntent,
    data: setupIntent,
    isPending: isCreatingIntent,
    reset: resetIntent,
  } = useCreateSetupIntent()

  // Auto-select the default payment method on first load
  useEffect(() => {
    if (!value && methods?.length) {
      const defaultMethod = methods.find((m) => m.isDefault) ?? methods[0]
      onChange(defaultMethod.id)
    }
  }, [methods, value, onChange])

  const handleAddCardClick = () => {
    createSetupIntent(memberId)
  }

  const handleCardAdded = () => {
    resetIntent()
    refetch()
  }

  const handleAddCardCancel = () => {
    resetIntent()
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    )
  }

  const tapToPayOption = allowTapToPay && (
    <TapToPayOption
      selected={value === TAP_TO_PAY_METHOD}
      onSelect={() => onChange(TAP_TO_PAY_METHOD)}
    />
  )

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,16rem),1fr))] gap-2">
        {(methods ?? []).map((method) => (
          <PaymentMethodOption
            key={method.id}
            method={method}
            selected={value === method.id}
            onSelect={() => onChange(method.id)}
          />
        ))}

        {tapToPayOption}

        {setupIntent?.clientSecret ? null : (
          <button
            type="button"
            onClick={handleAddCardClick}
            disabled={isCreatingIntent}
            className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            <Plus className="size-4" />
            {isCreatingIntent ? 'Loading…' : 'Add card'}
          </button>
        )}
      </div>

      {setupIntent?.clientSecret && (
        <AddCardTray
          setupIntent={setupIntent}
          onSuccess={handleCardAdded}
          onCancel={handleAddCardCancel}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

/**
 * The card entry tray, rendered in both the empty state and below the card grid.
 *
 * Exists to own the `key`: keying on the secret means a new SetupIntent — and so a
 * possible region change — remounts Elements from scratch, which is the only way the
 * swap can take effect, since react-stripe-js ignores a changed `stripe` prop on an
 * already-mounted instance.
 */
function AddCardTray({
  setupIntent,
  onSuccess,
  onCancel,
}: {
  setupIntent: PostMemberSetupIntentResponse
  onSuccess: () => void
  onCancel: () => void
}) {
  return (
    <AddPaymentMethodForm
      key={setupIntent.clientSecret}
      setupIntent={setupIntent}
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  )
}

/** Selectable tile for taking the payment in person via the Stripe Dashboard app. */
function TapToPayOption({ selected, onSelect }: { selected: boolean; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border hover:bg-muted/50'
      )}
    >
      <Smartphone className="size-5 shrink-0 text-muted-foreground" />
      <div className="flex-1 text-sm">
        <span className="font-medium">Tap to pay</span>
        <div className="text-sm text-muted-foreground">Stripe Dashboard app</div>
      </div>
    </button>
  )
}

/** A selectable payment method card showing brand, last 4, and expiry. */
function PaymentMethodOption({
  method,
  selected,
  onSelect,
}: {
  method: MemberPaymentMethod
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border hover:bg-muted/50'
      )}
    >
      <CreditCard className="size-5 shrink-0 text-muted-foreground" />
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="font-medium">{formatCardBrand(method)}</span>
          <span className="text-muted-foreground">{formatCardNumber(method)}</span>
          {method.isDefault && (
            <Badge size="xs" variant="info">
              Default
            </Badge>
          )}
        </div>
        <span className="text-sm text-muted-foreground">
          Expires {String(method.expMonth).padStart(2, '0')}/{method.expYear}
        </span>
      </div>
    </button>
  )
}
